<template>
    <div class="project-invitations">
        <div class="mb-3">
            <b-card-header class="d-flex align-items-center justify-content-between">
                <h3 class="mb-sm-0">Project Invitations</h3>
                <b-button
                    class="mt-2 mt-sm-0 btn-light"
                    variant="primary"
                    @click="addInvite">
                    Create Invite
                </b-button>
            </b-card-header>
            <b-card-body>
                <table class="table table-striped">
                    <tr v-if="addingInvite">
                        <td colspan="4">
                            <b-form-group label="Email">
                                <b-form-input v-model="invitationEmail"/>
                            </b-form-group>
                            <b-form-group label="Role">
                                <b-form-input v-model="invitationEmail"/>
                            </b-form-group>
                            <b-button
                                :disabled="!invitationEmail"
                                @click="submitInvite"
                                class="float-right mt-2"
                                variant="primary">
                                Submit
                            </b-button>
                            <b-button @click="cancelInvite" class="float-right mt-2 mr-2" variant="danger">Cancel
                            </b-button>
                        </td>
                    </tr>
                    <tr v-for="invite in invitations" :key="invite.id">
                        <td>{{ invite.email }}</td>
                        <td>{{ invite.role }}</td>
                        <td>
                            <b-badge :variant="statusVariant(invite.status)">{{ invite.status }}</b-badge>
                        </td>
                        <td>
                            <user-details v-if="invite.resolvedUser" :user="invite.resolvedUser"></user-details>
                        </td>
                        <td>
                            <b-button v-if="!invite.resolvedUser" @click="unInvite(invite)">Un-Invite</b-button>
                        </td>
                    </tr>
                    <tr v-if="invitations.length === 0">
                        No invitations have been sent for this project. Click 'Create Invite' to add contractors or project managers who don't yet have an account.
                    </tr>
                </table>
            </b-card-body>
        </div>

        <data-form-modal
            modal-id="project-invite-modal"
            save-button-label="Send"
            :field-list="['email', 'role']"
            :field-model="projectInviteModel"
            :model-instance="projectInvitation"
            :on-submit-handler="submitInvite">
        </data-form-modal>
    </div>
</template>

<script>
import {required, email as emailValidator} from 'vuelidate/lib/validators'
import Agency from "@/models/Agency";

export default {
    name: 'ProjectInvitations',
    props: {
        projectId: Number,
        agencyId: Number
    },
    data() {
        return {
            invitations: [],
            addingInvite: false,
            invitationEmail: '',
            projectInviteModel: {
                fields: {
                    email: {
                        type: 'text',
                        label: 'Email',
                        validations: {
                            required,
                            email: v => v ? emailValidator(v.toLowerCase()) : true,
                            invite_unique: async function (value) {
                                if (value) {
                                    return !(await this.$api.projectInvitationExists(value)).body
                                }
                                return true
                            }
                        },
                        invalidFeedback: {
                            required: 'Email is required',
                            invite_unique: 'There is already a user or pending invite with this Email'
                        }
                    },
                    role: {
                        type: 'select',
                        label: 'Role',
                        options: [
                            {value: 'CONTRACTOR', text: 'Primary Contractor'},
                            {value: 'SUBCONTRACTOR', text: 'Subcontractor'},
                            {value: 'PROJECT_MANAGER', text: 'Project Manager'},
                            {value: 'OWNERS_REPRESENTATIVE', text: 'Owners Representative'},
                        ],
                        validations: {required}
                    },
                }
            },
            projectInvitation: {},
        }
    },
    methods: {
        addInvite() {
            if(this.agencyId) {
                this.projectInvitation = {}
                this.$bvModal.show('project-invite-modal')
            } else {
                this.$bvModal.msgBoxOk("Before sending invitations, you must assign an agency to the project. Invited project managers will be assigned to the selected agency.");
            }
        },
        async unInvite(invite) {
            this.$bvModal.msgBoxConfirm('Would you like to un-invite this user?')
                .then(async value => {
                    if (value) {
                        this.invitations = this.invitations.filter(function(i) {
                            return i.id != invite.id
                        })
                        await this.$api.deleteItem(this, 'project-invitation', invite).then((response) => {
                                this.$toasted.show("Invitation deleted")
                            }, (error) => {
                                this.$toasted.show("Error deleting invitation")
                        })
                    }
                })
        },
        cancelInvite() {
            this.addingInvite = false
            this.invitationEmail = ''
        },
        submitInvite() {
            this.projectInvitation.project_id = this.projectId
            this.projectInvitation.agency_id = this.agencyId

            this.invitations.push(this.projectInvitation)

            this.$api.createItem(this, 'project-invitation', this.projectInvitation).then((response) => {
                this.$toasted.show("Invitation sent")
                this.refresh()
            }, (error) => {
                this.$toasted.show("Error sending invitation")
            })

            this.projectInvitation = {}
        },
        statusVariant(status) {
            if (status === 'PENDING') {
                return 'warning'
            } else if (status === 'REMOVED') {
                return 'danger'
            } else if (status === 'RESOLVED') {
                return 'success'
            }
        },
        async refresh() {
            const filters = {project_id: {field: 'project_id', value: this.projectId}}
            const response = await this.$api.getItems(this, 'project-invitation', filters)
            this.invitations = response ?? []
        }
    },
    mounted() {
        this.refresh()
    }
}
</script>

<style lang="scss">

.comment-area {
    background-color: #eeeeee;
}

#project-invite-modal {
    header {
        justify-content: center;
        align-items: center;
        > .row {
            justify-content: center;
            align-items: center;
            width: 100%;
            max-width: 170px;
            margin: 0 auto;

            > .col-6 {
                display: none;
            }

            > .col-3 {
                max-width: 50%;
            }

            > div:nth-child(2) .btn {
                background-color: var(--red);
                border:none;

                &:hover {
                    background-color: var(--red-lighten-10);
                }

                span {
                    color: #fff;
                }
            }

            > div:nth-child(3) .btn-light {
                background-color: var(--green);
                border-radius: 4px;
                padding: 6px 12px;
                line-height: 1.5 !important;

                &:hover {
                    background-color: var(--green-lighten-5) !important;
                }

                span {
                    color: #fff;
                }
            }


        }
    }
}

</style>
